import { useCallback, useEffect, useRef, useState } from 'react'
import { agentService, CONFIG_FALLBACK } from '../services/agentService'
import { agentSocketUrl } from '../lib/agentUrls'
import { openAgentSocket, type AgentSocket } from '../lib/agentSocket'
import { closeMessage } from '../lib/closeReason'
import { reconnectDelayMs } from '../lib/reconnect'
import { startCapture, type Capture } from '../lib/audioCapture'
import { createPlayback, type Playback } from '../lib/audioPlayback'
import type { AgentConfig, AgentMessage } from '../types'

/**
 * `idle`         nothing has happened yet — the customer has not tapped
 * `connecting`   microphone opening, then socket
 * `live`         in conversation
 * `reconnecting` dropped, trying again, microphone still open
 * `ended`        the customer hung up, deliberately
 * `failed`       it stopped for good without them asking
 */
export type SessionPhase =
  | 'idle' | 'connecting' | 'live' | 'reconnecting' | 'ended' | 'failed'

/**
 * The order lives in the agent's memory for the life of one connection, so a
 * reconnect always comes back to an agent that remembers nothing — while the
 * customer believes it remembers everything they said. The page must not hide
 * that. The session layer is the real fix and is not in this sprint.
 */
const ORDER_LOST =
  "Sorry — the connection dropped and your order was lost. Could you tell me again?"

export interface AgentSession {
  phase: SessionPhase
  config: AgentConfig
  /** Only set in `failed`. Already made safe to show a stranger. */
  error: string | null
  /** Something the customer must act on, e.g. their order needs repeating. */
  notice: string | null
  dismissNotice: () => void
  /** Live while a session is up, for the orb. Null before and after. */
  analysers: { mic: AnalyserNode; bot: AnalyserNode } | null
  start: () => void
  end: () => void
  interrupt: () => void
}

interface Options {
  /** The page's own query string, forwarded to the socket untouched. */
  search: string
  onMessage?: (msg: AgentMessage) => void
}

export function useAgentSession({ search, onMessage }: Options): AgentSession {
  const [phase, setPhase] = useState<SessionPhase>('idle')
  const [config, setConfig] = useState<AgentConfig>(CONFIG_FALLBACK)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [analysers, setAnalysers] = useState<AgentSession['analysers']>(null)

  const socketRef = useRef<AgentSocket | null>(null)
  const ctxRef = useRef<AudioContext | null>(null)
  const captureRef = useRef<Capture | null>(null)
  const playbackRef = useRef<Playback | null>(null)
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const attemptRef = useRef(0)

  // Latched after the commit, not during render, so socket callbacks never
  // close over a stale one. The socket only opens on a tap, long after.
  const onMessageRef = useRef(onMessage)
  const configRef = useRef(config)
  useEffect(() => {
    onMessageRef.current = onMessage
    configRef.current = config
  })

  // Read at load, not baked into the build: this is the agent's operational
  // switchboard and ops flips it without a frontend redeploy. Never rejects.
  useEffect(() => {
    let cancelled = false
    agentService.config().then((c) => { if (!cancelled) setConfig(c) })
    return () => { cancelled = true }
  }, [])

  const teardown = useCallback(() => {
    if (retryTimerRef.current) clearTimeout(retryTimerRef.current)
    retryTimerRef.current = null
    attemptRef.current = 0
    socketRef.current?.close()
    socketRef.current = null
    captureRef.current?.stop()
    captureRef.current = null
    playbackRef.current?.dispose()
    playbackRef.current = null
    void ctxRef.current?.close()
    ctxRef.current = null
    setAnalysers(null)
  }, [])

  const end = useCallback(() => {
    // Closing the socket is what makes the server upload the call recording
    teardown()
    setPhase('ended')
  }, [teardown])

  const start = useCallback(() => {
    if (ctxRef.current) return
    setError(null)
    setNotice(null)
    attemptRef.current = 0
    setPhase('connecting')

    // Created synchronously, inside the tap. Browsers refuse audio and the
    // microphone without a user gesture, and an AudioContext built in a later
    // async tick has already lost that gesture.
    const ctx = new AudioContext()
    ctxRef.current = ctx

    // A local function declaration so a reconnect can call it by name from
    // inside its own close handler. The microphone is NOT rebuilt on a
    // reconnect: the audio graph outlives the socket, so the customer is not
    // asked for permission twice and the orb does not blink out.
    function connect(): void {
      socketRef.current = openAgentSocket(agentSocketUrl(search), {
        onOpen: () => {
          if (attemptRef.current > 0) setNotice(ORDER_LOST)
          attemptRef.current = 0
          setPhase('live')
        },
        onMessage: (msg) => onMessageRef.current?.(msg),
        onAudio: (pcm) => playbackRef.current?.push(pcm),
        onClose: (closed) => {
          socketRef.current = null
          // Whatever was queued belongs to a conversation that has ended.
          playbackRef.current?.stopAll()

          const delay = reconnectDelayMs(closed, attemptRef.current)

          if (delay === null) {
            // Release the microphone and the context, or "Try again" would find
            // a session already in progress and do nothing.
            teardown()
            setError(closeMessage(closed) ?? 'Please scan the code again to start over.')
            setPhase('failed')
            return
          }

          attemptRef.current += 1
          setPhase('reconnecting')
          retryTimerRef.current = setTimeout(connect, delay)
        },
      })
    }

    void (async () => {
      const cfg = configRef.current
      try {
        await ctx.resume()

        // Audio is set up before the socket opens, because the agent greets the
        // customer the instant it connects — with no playback ready, the first
        // thing it says is lost.
        const playback = createPlayback(ctx, {
          playbackLeadMs: cfg.playback_lead_ms,
          ttsTailMs: cfg.tts_tail_ms,
        })
        playbackRef.current = playback

        const capture = await startCapture(ctx, {
          onBlock: (pcm) => {
            // The speech gate. Blocks keep arriving while the agent talks; they
            // are dropped rather than sent.
            if (playbackRef.current?.isBotSpeaking()) return
            socketRef.current?.sendAudio(pcm.buffer)
          },
        })
        captureRef.current = capture
        setAnalysers({ mic: capture.analyser, bot: playback.analyser })
      } catch {
        // Overwhelmingly this is the customer declining the microphone.
        teardown()
        setError('Voice ordering needs the microphone. Allow it and tap to try again.')
        setPhase('failed')
        return
      }

      connect()
    })()
  }, [search, teardown])

  const interrupt = useCallback(() => {
    // Local stop first — the agent has already paced that audio onto the wire,
    // so only we can kill it, and killing it here is what makes it feel instant.
    playbackRef.current?.stopAll()
    socketRef.current?.interrupt()
  }, [])

  // Android suspends the AudioContext when the screen locks or the customer
  // switches apps, so capture stops whether or not this is handled. The socket
  // is held open across it; only the audio needs waking.
  useEffect(() => {
    function onVisibility(): void {
      const ctx = ctxRef.current
      if (document.hidden || !ctx || ctx.state !== 'suspended') return
      void ctx.resume().then(() => {
        // currentTime froze while suspended, so anything still queued is
        // scheduled against a clock that no longer means anything. Drop it and
        // let the play head start again from now.
        playbackRef.current?.stopAll()
      })
    }
    document.addEventListener('visibilitychange', onVisibility)
    return () => document.removeEventListener('visibilitychange', onVisibility)
  }, [])

  // A customer who closes the tab should not leave a socket holding a pipeline
  // open — the agent has no idle timeout to clean up after them.
  useEffect(() => () => teardown(), [teardown])

  const dismissNotice = useCallback(() => setNotice(null), [])

  return {
    phase, config, error, notice, dismissNotice, analysers,
    start, end, interrupt,
  }
}
